import { ReduxUpdater } from './ReduxUpdater';
import type { ControlPosition } from 'react-draggable';
import type { IUserStampInfo, OAuthUserDataType } from '../types';
import type { Core } from '@pdftron/webviewer';
import { ApryseSDK } from './ApryseSDK';
import { findClosestNumber } from '../utils';
import { GlobalConstants } from './GlobalConstants';

export class GlobalVariables {
    static isForTestRun = false;
    static bookmarks: Core.Bookmark[] = [];
    static contextMenuAnchor: Element | null = null;
    static copiedText = '';
    static disableKeyboardShortcuts = false;
    static documentUnsaved = false;
    static isDefaultCategorySelected = true;
    static isTextEditing = false;
    static licenseKey: string;
    static selectedText = '';
    static readonly stampDialogPosition: ControlPosition = { x: 75, y: 155 };
    static reportedDevErrors: string[] = [];
    static reportedDevLogs: string[] = [];
    static reportedDevWarnings: string[] = [];

    /** Holds the whole user profile data */
    static authenticatedUser: OAuthUserDataType | null = null;

    static rotationAngles: { [id: string]: number } = {};

    static readonly isVisualStudioCode =
        process.env.NODE_ENV === 'development' && !process.env.REACT_APP_IS_VS;

    static userStamp: IUserStampInfo = GlobalConstants.emptyUserStamp;
    static userStampFile: File | null = null;

    /** Returns adCommonName of the authenticated user */
    static get ownersName(): string {
        if (!GlobalVariables.authenticatedUser) {
            return 'Unknown';
        }
        return GlobalVariables.authenticatedUser.adCommonName;
    }

    static readonly StampSelectorID = 'stampSelector';

    static registeredCommands = {
        selectAll: ApryseSDK.commonSelectAll,
        copy: ApryseSDK.commonCopy,
        paste: ApryseSDK.commonPaste,
        cut: ApryseSDK.commonCut,
        //copyTextToClipboard: copySelectedTextFromDocument,
        openSearchPanel: () => {
            ReduxUpdater.selectedSidepanel = 'search';
        },
    };

    /**
     * The available zoom levels.
     */
    static readonly zoomLevels: ReadonlyArray<number> = [
        0.1, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 4, 6, 10,
    ];

    /**
     * Zooms the document, optionally at the given device position.
     * @param scale The target scale or direction.
     * @param devicePosition The device position relative to the viewport.
     */
    static async zoom(scale: number | 'in' | 'out', annotation?: Core.Annotations.Annotation) {
        const currentZoom = parseFloat(ApryseSDK.documentViewer.getZoomLevel().toString());
        const scaleValue = scale === 'in' ? 1 : -1;
        const targetZoom = Math.max(
            Math.min(
                typeof scale === 'string'
                    ? findClosestNumber(GlobalVariables.zoomLevels, currentZoom, scaleValue)
                    : scale,
                ApryseSDK.instance.UI.getMaxZoomLevel(),
            ),
            ApryseSDK.instance.UI.getMinZoomLevel(),
        );

        if (currentZoom === targetZoom) {
            return;
        }

        if (annotation) {
            if (annotation.PageNumber !== ApryseSDK.documentViewer.getCurrentPage()) {
                ApryseSDK.documentViewer.setCurrentPage(annotation.PageNumber, true);
            }
            const jumpToAnnotationOptions = {
                zoom: currentZoom.toFixed(2), // (typeof scale !== 'string' && scale > 0.0 ? scale : currentScale).toFixed(2),
                fitToView: false, // typeof scale !== 'string' && scale === -1,
                isSmoothScroll: true,
            };
            ApryseSDK.annotationManager.jumpToAnnotation(annotation, jumpToAnnotationOptions);
            return;
        }

        const scrollView = ApryseSDK.documentViewer.getScrollViewElement();

        // Current scroll position
        const scrollLeft = scrollView.scrollLeft;
        const scrollTop = scrollView.scrollTop;

        // Viewport size
        const viewWidth = scrollView.clientWidth;
        const viewHeight = scrollView.clientHeight;

        // Center point in page coordinates before zoom
        const centerX = (scrollLeft + viewWidth / 2) / currentZoom;
        const centerY = (scrollTop + viewHeight / 2) / currentZoom;

        // Apply new zoom
        ApryseSDK.documentViewer.zoomTo(targetZoom);

        // Recalculate scroll to keep the center point fixed
        scrollView.scrollLeft = centerX * targetZoom - viewWidth / 2;
        scrollView.scrollTop = centerY * targetZoom - viewHeight / 2;
    }
}
